import * as _ from "lodash";
import { LgConsole } from "@logex/framework/core";
import { Observable, of, Subject, BehaviorSubject, AsyncSubject, Subscription } from "rxjs";
import {
    catchError,
    multicast,
    switchMap,
    tap,
    finalize,
    delay,
    defaultIfEmpty,
    refCount
} from "rxjs/operators";
import {
    LoaderConfiguration,
    LoaderArguments,
    LoaderArgumentsMap,
    LoaderCallback
} from "./configuration";
import { ILoadManager } from "./load-manager";

// ----------------------------------------------------------------------------------
//
enum LoaderLoadingState {
    Idle = 0,
    AwaitingSubscribers = 1,
    LoadingDependencies = 2,
    LoadingData = 3
}

export class Loader<TResult = any> {
    // ----------------------------------------------------------------------------------
    // Fields
    private _loader: LoaderCallback<TResult>;
    private _storeResult: boolean;
    private _state: LoaderLoadingState = LoaderLoadingState.Idle;
    private _loadingArgs: any[];
    private _lastArgs: any[];
    private _results: any = undefined;
    private _connection: Subscription;
    private _loadingObservable: Observable<TResult>;

    private _dataSubject: Subject<TResult>;
    private _dataObservable: Observable<TResult>;
    private _dataObserversCount: number;

    private _isLoadingSubject: Subject<boolean>;
    private _isLoadingObservable: Observable<boolean>;

    noCache: boolean;

    // ----------------------------------------------------------------------------------
    //
    constructor(
        private _master: ILoadManager,
        public name: string,
        public cfg: LoaderConfiguration<TResult>,
        private _console: LgConsole
    ) {
        this._loader = cfg.loader;
        this.noCache = cfg.noCache;
        this._storeResult = cfg.storeResult;

        this._isLoadingSubject = new BehaviorSubject(false);
        this._isLoadingObservable = this._isLoadingSubject.pipe(
            multicast(new Subject<boolean>()),
            refCount()
        );
    }

    load(argsOverride: LoaderArguments, forceReload: boolean): Observable<TResult> {
        const args = this._getArguments(this.cfg.args || [], forceReload, argsOverride);

        this._console.debug(`Requested dataset: ${this.name}`, args);

        if (this._state > LoaderLoadingState.Idle) {
            // Check that we are loading with the same arguments.
            if (_.isEqual(args, this._loadingArgs)) {
                // If yes - return the same multicast observable
                this._console.debug(`"${this.name}" with such arguments is being loaded`);
                return this._loadingObservable;
            } else {
                // If not - cancel loading
                this._console.debug(
                    `"${this.name}" is being loaded width other arguments, cancelling`,
                    args,
                    this._loadingArgs,
                    _.isEqual(args, this._loadingArgs)
                );
                this.cancel();
            }
        }

        if (!forceReload) {
            forceReload = this.noCache;
        }

        if (!forceReload && !this._isChangedArguments(args)) {
            this._console.debug(`"${this.name}" with such params was already loaded`);
            if (this._storeResult) {
                this._notifyDataObservers(this._results);
                return of(this._results);
            } else {
                return of(null);
            }
        }

        this._loadingArgs = args;
        this._state = LoaderLoadingState.AwaitingSubscribers;

        const data$ = of(true).pipe(
            tap(() => {
                this._console.debug("Start loading dependencies");
                this._state = LoaderLoadingState.LoadingDependencies;
                this._isLoadingSubject.next(true);
            }),

            // Load requires
            switchMap(() => {
                // Required data sets
                const required$ = !_.isEmpty(this.cfg.require)
                    ? !forceReload
                        ? this._master.load(...this.cfg.require)
                        : this._master.reload(...this.cfg.require)
                    : of([]);

                return required$.pipe(defaultIfEmpty([]));
            }),

            delay(this.cfg.delay || 0),

            switchMap(() => {
                this._console.debug(`Loading "${this.name}"`, args);
                this._state = LoaderLoadingState.LoadingData;
                this._loadingArgs = args;
                return this._loader(...args);
            }),

            tap(res => {
                this._console.debug(`Loaded "${this.name}"`);
                this._lastArgs = this._loadingArgs;
                if (this._storeResult) {
                    this._results = res;
                }
                this._isLoadingSubject.next(false);
                this._notifyDataObservers(res);
            }),

            catchError((err, caught) => {
                this._console.debug(`Error loading "${this.name}"`, err);
                this._lastArgs = undefined;
                this._results = undefined;
                this._isLoadingSubject.next(false);

                throw err;
            }),

            finalize(() => {
                this._reset();
                this._console.debug(`Finished loading "${this.name}"`);
                this._isLoadingSubject.next(false); // todo: remove the other 2 in tap and catchError?
            })
        );

        const multicasted = multicast(new AsyncSubject())(data$);

        let refCount = 0;
        const self = this;

        // Start loading
        const connection = multicasted.connect();
        this._connection = connection;
        self._console.debug(`Connected to loader`);

        this._loadingObservable = new Observable(subscriber => {
            ++refCount;
            self._console.debug(`Subscribed to loader (refCount == ${refCount})`);

            const res = multicasted.subscribe(subscriber);

            return {
                ...res,
                unsubscribe() {
                    refCount--;
                    self._console.debug(`Unsubscribing from loader (refCount == ${refCount})`);

                    res.unsubscribe();

                    if (refCount === 0) {
                        connection.unsubscribe();
                    }
                }
            };
        });

        return this._loadingObservable;
    }

    private _getArguments(
        args: LoaderArguments,
        forceReload: boolean,
        argsOverride?: LoaderArguments
    ) {
        function mapArray(argsArray: LoaderArguments) {
            return _.map(argsArray, x => {
                if (_.isFunction(x)) {
                    return x(forceReload);
                } else if (_.isObject(x)) {
                    return mapDictionary(x);
                } else {
                    return x;
                }
            });
        }

        function mapDictionary(argsMap: LoaderArgumentsMap) {
            return _.mapValues(argsMap, x => x(forceReload));
        }

        // Override arguments if requested
        if (!_.isEmpty(argsOverride)) {
            args = _.cloneDeep(args);
            _.each(argsOverride, (x, i) => {
                if (x === undefined) return;
                if (_.isFunction(x)) {
                    args[i] = x;
                } else if (_.isObject(x)) {
                    if (!_.isObject(args[i])) {
                        args[i] = {};
                    }
                    _.each(x, (val, name) => {
                        if (val !== undefined) args[i][name] = val;
                    });
                } else {
                    args[i] = x;
                }
            });
        }

        return mapArray(args);
    }

    private _isChangedArguments(args: any[]) {
        return this._lastArgs === undefined || !_.isEqual(args, this._lastArgs);
    }

    public get isLoading() {
        return this._state > LoaderLoadingState.LoadingDependencies;
    }

    public get isLoaded() {
        return !this.isLoading && this._lastArgs !== undefined;
    }

    public get isLoadingAsObservable() {
        return this._isLoadingObservable;
    }

    public get lastArgs() {
        return this._lastArgs;
    }

    public clearLastArgs() {
        this._lastArgs = undefined;
    }

    cancel() {
        if (this._state === LoaderLoadingState.Idle) {
            return;
        }

        if (this._connection != null) {
            this._connection.unsubscribe();
            this._connection = null;
        }

        this._reset();

        this._console.debug(`Cancelled loading of "${this.name}"`);
    }

    private _reset() {
        this._state = LoaderLoadingState.Idle;
        this._loadingArgs = undefined;
        this._loadingObservable = null;
        this._connection = null;
    }

    dataAsObservable(): Observable<TResult> {
        if (this._dataObservable == null) {
            this._createDataObservable();
        }

        return this._dataObservable;
    }

    private _createDataObservable() {
        if (!this.cfg.storeResult) {
            throw Error(
                "dataAsObservable is available only when loader stores result (storeResult: true)."
            );
        }

        this._dataObserversCount = 0;
        this._dataSubject = new Subject();

        const multicasted = multicast(new Subject<TResult>())(this._dataSubject);
        multicasted.connect();

        const self = this;
        this._dataObservable = new Observable(subscriber => {
            ++self._dataObserversCount;
            self._console.debug(
                `Subscribed to data observable (refCount == ${self._dataObserversCount})`
            );

            // If this is a first subscriber - initiate loading
            if (self._dataObserversCount === 1) {
                this._console.debug("First subscriber - trigger load()");
                Promise.resolve().then(() => {
                    self.load(null, false);
                });
            } else {
                // Not the first subscriber
                if (self._results !== undefined && !self.isLoading) {
                    this._console.debug("Not the first subscriber - give last results.");
                    Promise.resolve().then(() => {
                        subscriber.next(self._results);
                    });
                } else {
                    this._console.debug(
                        "Not the first subscriber, no previous results - wait for load() to complete."
                    );
                }
            }

            const res = multicasted.subscribe(subscriber);

            return {
                ...res,
                unsubscribe() {
                    self._dataObserversCount--;
                    self._console.debug(
                        `Unsubscribing from data observable (refCount == ${self._dataObserversCount})`
                    );

                    res.unsubscribe();
                }
            };
        });
    }

    private _notifyDataObservers(res) {
        if (this._dataSubject != null) {
            this._console.debug(`Notify observers of "${this.name}"`);
            this._dataSubject.next(res);
        }
    }

    public isObserved() {
        return this._dataObserversCount > 0;
    }
}
